import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import AppLayout from '@/components/layout/AppLayout';
import { ROUTES } from '@/config/constants';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const OrdersPage = lazy(() => import('@/pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/orders/OrderDetailPage'));
const RestaurantsPage = lazy(() => import('@/pages/restaurants/RestaurantsPage'));
const NewRestaurantPage = lazy(() => import('@/pages/restaurants/NewRestaurantPage'));
const RestaurantCreatedPage = lazy(() => import('@/pages/restaurants/RestaurantCreatedPage'));
const RestaurantProfilePage = lazy(() => import('@/pages/restaurants/RestaurantProfilePage'));
const RestaurantReviewPage = lazy(() => import('@/pages/restaurants/RestaurantReviewPage'));
const RestaurantEnabledPage = lazy(() => import('@/pages/restaurants/RestaurantEnabledPage'));
const MenuItemFormPage = lazy(() => import('@/pages/restaurants/MenuItemFormPage'));
const DeliveryPartnersPage = lazy(() => import('@/pages/delivery-partners/DeliveryPartnersPage'));
const NewDeliveryPartnerPage = lazy(() => import('@/pages/delivery-partners/NewDeliveryPartnerPage'));
const DeliveryPartnerProfilePage = lazy(() => import('@/pages/delivery-partners/DeliveryPartnerProfilePage'));
const DeliveryPartnerReviewPage = lazy(() => import('@/pages/delivery-partners/DeliveryPartnerReviewPage'));
const DeliveryPartnerEnabledPage = lazy(() => import('@/pages/delivery-partners/DeliveryPartnerEnabledPage'));
const CustomersPage = lazy(() => import('@/pages/customers/CustomersPage'));
const CustomerProfilePage = lazy(() => import('@/pages/customers/CustomerProfilePage'));
const CustomerOrderDetailPage = lazy(() => import('@/pages/customers/CustomerOrderDetailPage'));
const SubscriptionsPage = lazy(() => import('@/pages/subscriptions/SubscriptionsPage'));
const ManagePlansPage = lazy(() => import('@/pages/subscriptions/ManagePlansPage'));
const PlanFormPage = lazy(() => import('@/pages/subscriptions/PlanFormPage'));
const WalletFinancePage = lazy(() => import('@/pages/wallet-finance/WalletFinancePage'));
const PromotionsPage = lazy(() => import('@/pages/promotions/PromotionsPage'));
const CouponFormPage = lazy(() => import('@/pages/promotions/CouponFormPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const SupportTicketsPage = lazy(() => import('@/pages/support-tickets/SupportTicketsPage'));
const TicketDetailPage = lazy(() => import('@/pages/support-tickets/TicketDetailPage'));
const ServiceZonesPage = lazy(() => import('@/pages/service-zones/ServiceZonesPage'));
const AddCityPage = lazy(() => import('@/pages/service-zones/AddCityPage'));
const CreateZonePage = lazy(() => import('@/pages/service-zones/CreateZonePage'));
const ZoneDetailPage = lazy(() => import('@/pages/service-zones/ZoneDetailPage'));
const AdminsPage = lazy(() => import('@/pages/admins/AdminsPage'));
const RoleFormPage = lazy(() => import('@/pages/admins/RoleFormPage'));
const RolePermissionsPage = lazy(() => import('@/pages/admins/RolePermissionsPage'));
const StaffPage = lazy(() => import('@/pages/admins/StaffPage'));
const InviteStaffPage = lazy(() => import('@/pages/admins/InviteStaffPage'));
const StaffProfilePage = lazy(() => import('@/pages/admins/StaffProfilePage'));
const BannersCategoriesPage = lazy(() => import('@/pages/banners-categories/BannersCategoriesPage'));
const CategoryFormPage = lazy(() => import('@/pages/banners-categories/CategoryFormPage'));
const BannersPage = lazy(() => import('@/pages/banners-categories/BannersPage'));
const BannerFormPage = lazy(() => import('@/pages/banners-categories/BannerFormPage'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const NotificationFormPage = lazy(() => import('@/pages/notifications/NotificationFormPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const InvoiceGSTPage = lazy(() => import('@/pages/invoice-gst/InvoiceGSTPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/invoice-gst/InvoiceDetailPage'));
const OpsAdminPage = lazy(() => import('@/pages/ops-admin/OpsAdminPage'));

const PageLoader = () => (
  <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Spin size="large" />
  </div>
);

const wrap = (component: React.ReactNode) => (
  <Suspense fallback={<PageLoader />}>{component}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
  {
    element: <PublicRoute />,
    children: [
      { path: ROUTES.LOGIN, element: wrap(<LoginPage />) },
      { path: ROUTES.FORGOT_PASSWORD, element: wrap(<ForgotPasswordPage />) },
    ],
  },
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: ROUTES.DASHBOARD, element: wrap(<DashboardPage />) },
          { path: ROUTES.ORDERS, element: wrap(<OrdersPage />) },
          { path: `${ROUTES.ORDERS}/:id`, element: wrap(<OrderDetailPage />) },
          { path: ROUTES.RESTAURANTS, element: wrap(<RestaurantsPage />) },
          { path: ROUTES.NEW_RESTAURANT, element: wrap(<NewRestaurantPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id/setup`, element: wrap(<NewRestaurantPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id/created`, element: wrap(<RestaurantCreatedPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id/review`, element: wrap(<RestaurantReviewPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id/enabled`, element: wrap(<RestaurantEnabledPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id/menu/new`, element: wrap(<MenuItemFormPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id/menu/:itemId`, element: wrap(<MenuItemFormPage />) },
          { path: `${ROUTES.RESTAURANTS}/:id`, element: wrap(<RestaurantProfilePage />) },
          { path: ROUTES.DELIVERY_PARTNERS, element: wrap(<DeliveryPartnersPage />) },
          { path: ROUTES.NEW_DELIVERY_PARTNER, element: wrap(<NewDeliveryPartnerPage />) },
          { path: `${ROUTES.DELIVERY_PARTNERS}/:id/review`, element: wrap(<DeliveryPartnerReviewPage />) },
          { path: `${ROUTES.DELIVERY_PARTNERS}/:id/enabled`, element: wrap(<DeliveryPartnerEnabledPage />) },
          { path: `${ROUTES.DELIVERY_PARTNERS}/:id`, element: wrap(<DeliveryPartnerProfilePage />) },
          { path: ROUTES.CUSTOMERS, element: wrap(<CustomersPage />) },
          { path: `${ROUTES.CUSTOMERS}/:id/orders/:orderId`, element: wrap(<CustomerOrderDetailPage />) },
          { path: `${ROUTES.CUSTOMERS}/:id`, element: wrap(<CustomerProfilePage />) },
          { path: ROUTES.SUBSCRIPTIONS, element: wrap(<SubscriptionsPage />) },
          { path: ROUTES.SUBSCRIPTION_PLANS, element: wrap(<ManagePlansPage />) },
          { path: ROUTES.NEW_SUBSCRIPTION_PLAN, element: wrap(<PlanFormPage />) },
          { path: `${ROUTES.SUBSCRIPTION_PLANS}/:id/edit`, element: wrap(<PlanFormPage />) },
          { path: ROUTES.WALLET_FINANCE, element: wrap(<WalletFinancePage />) },
          { path: ROUTES.PROMOTIONS, element: wrap(<PromotionsPage />) },
          { path: ROUTES.NEW_COUPON, element: wrap(<CouponFormPage />) },
          { path: `${ROUTES.PROMOTIONS}/:id/edit`, element: wrap(<CouponFormPage />) },
          { path: ROUTES.REPORTS, element: wrap(<ReportsPage />) },
          { path: ROUTES.SUPPORT_TICKETS, element: wrap(<SupportTicketsPage />) },
          { path: `${ROUTES.SUPPORT_TICKETS}/:id`, element: wrap(<TicketDetailPage />) },
          { path: ROUTES.SERVICE_ZONES, element: wrap(<ServiceZonesPage />) },
          { path: ROUTES.NEW_SERVICE_CITY, element: wrap(<AddCityPage />) },
          { path: ROUTES.NEW_SERVICE_ZONE, element: wrap(<CreateZonePage />) },
          { path: `${ROUTES.SERVICE_ZONES}/:id/edit`, element: wrap(<CreateZonePage />) },
          { path: `${ROUTES.SERVICE_ZONES}/:id`, element: wrap(<ZoneDetailPage />) },
          { path: ROUTES.NEW_ROLE, element: wrap(<RoleFormPage />) },
          { path: `${ROUTES.ADMINS}/roles/:id/edit`, element: wrap(<RoleFormPage />) },
          { path: `${ROUTES.ADMINS}/roles/:id/permissions`, element: wrap(<RolePermissionsPage />) },
          { path: ROUTES.INVITE_STAFF, element: wrap(<InviteStaffPage />) },
          { path: `${ROUTES.STAFF}/:id/edit`, element: wrap(<InviteStaffPage />) },
          { path: `${ROUTES.STAFF}/:id`, element: wrap(<StaffProfilePage />) },
          { path: ROUTES.STAFF, element: wrap(<StaffPage />) },
          { path: ROUTES.ADMINS, element: wrap(<AdminsPage />) },
          { path: ROUTES.BANNERS_CATEGORIES, element: wrap(<BannersCategoriesPage />) },
          { path: ROUTES.NEW_FOOD_CATEGORY, element: wrap(<CategoryFormPage />) },
          { path: `${ROUTES.BANNERS_CATEGORIES}/categories/:id/edit`, element: wrap(<CategoryFormPage />) },
          { path: ROUTES.MOBILE_BANNERS, element: wrap(<BannersPage />) },
          { path: ROUTES.NEW_MOBILE_BANNER, element: wrap(<BannerFormPage />) },
          { path: `${ROUTES.MOBILE_BANNERS}/:id/edit`, element: wrap(<BannerFormPage />) },
          { path: `${ROUTES.MOBILE_BANNERS}/:id`, element: wrap(<BannerFormPage />) },
          { path: ROUTES.NOTIFICATIONS, element: wrap(<NotificationsPage />) },
          { path: ROUTES.NEW_NOTIFICATION, element: wrap(<NotificationFormPage />) },
          { path: ROUTES.SETTINGS, element: wrap(<SettingsPage />) },
          { path: ROUTES.INVOICE_GST, element: wrap(<InvoiceGSTPage />) },
          { path: `${ROUTES.INVOICE_GST}/:id`, element: wrap(<InvoiceDetailPage />) },
          { path: ROUTES.OPS_ADMIN, element: wrap(<OpsAdminPage />) },
          // legacy redirect
          { path: '/users', element: <Navigate to={ROUTES.ADMINS} replace /> },
          { path: '/analytics', element: <Navigate to={ROUTES.REPORTS} replace /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
]);
